import * as CL from '@design-system/component-library';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { SelectedPurposeOfUseOrContact } from '../../common/enums.js';
import { t, userInformationAddedMsg, userInformationUpdatedMsg } from '../../common/i18n/index.js';
import { scrollTo } from '../../common/utils/browserUtils.js';
import { deepEqual } from '../../common/utils/objectUtils.js';
import { isInBrowser } from '../../common/utils/ssrUtils.js';
import { ContactCreationType, formValuesToPurposeOfUseOrContact } from './ContactsOrPurposeOfUseUtils.js';
import { ContactOrPurposeOfUseSelector } from './components/ContactOrPurposeOfUseSelector.js';
import { CopyUserInfoButton } from './components/CopyUserInfoButton.js';
import { CostCenterAndEmployeeNumber } from './components/CostCenterAndEmployeeNumber.js';
import type { ContactOrPurposeOfUseFormValues } from './ContactsOrPurposeOfUseUtils.js';
import type { PurposeOfUseOrContact } from '../../common/types/subscription.js';
import type { Contact } from '../../generated/api/models.js';
import type { ForwardedRef } from 'react';
import type { FieldErrors } from 'react-hook-form';

import './ContactOrPurposeOfUse.scss';

export interface ContactOrPurposeOfUseProps {
  id: string;
  isCopiedUserInfo?: boolean; // Signifies if the purposeOfUseOrContact prop is a result of copy user info

  // Data
  contacts?: Contact[];
  // Initial values. If this changes after first load, then form is reinitialized
  purposeOfUseOrContact: PurposeOfUseOrContact;

  // Handlers
  onChangeContact?: (selectedContactId?: string) => void;
  onCopyUserInfo?: (purposeOfUseOrContact: PurposeOfUseOrContact) => void;

  // Validations
  required?: boolean;
  isEmailAndPhoneRequired?: boolean;
  isCostCenterRequired?: boolean;

  // Config
  enableCostCenterAndReference?: boolean;
  onlyPurposeOfUse?: boolean;
}

export interface ContactOrPurposeOfUseCallables {
  // Gives the form values if valid. Otherwise scrolls to error if specified and returns false.
  getValues: (scrollToError: boolean) => Promise<PurposeOfUseOrContact | false>;
}

const purposeOfUseOrContactToFormValues = (
  purposeOfUseOrContact: PurposeOfUseOrContact
): ContactOrPurposeOfUseFormValues => ({
  selectionTab: purposeOfUseOrContact.selected || SelectedPurposeOfUseOrContact.CONTACT,
  selectContact: purposeOfUseOrContact.contactId || '',
  purposeOfUse: purposeOfUseOrContact.purposeOfUse || '',
  firstName: purposeOfUseOrContact.firstName || '',
  lastName: purposeOfUseOrContact.lastName || '',
  phoneNumber: purposeOfUseOrContact.phoneNumber || '',
  email: purposeOfUseOrContact.email || '',
  costCenter: purposeOfUseOrContact.costCenter || '',
  employeeNumber: purposeOfUseOrContact.employeeNumber || '',
});

interface CopyUserInfoSuccessMessageProps {
  purposeOfUseOrContact: PurposeOfUseOrContact;
}
const CopyUserInfoSuccessMessage = ({ purposeOfUseOrContact }: CopyUserInfoSuccessMessageProps) => (
  <div className="copy-user-info-success-message">
    <CL.Icon icon="updateRegular" size="s" />
    {purposeOfUseOrContact.contactId === ContactCreationType.COPIED_CONTACT
      ? t.ER2E(userInformationAddedMsg)
      : t.AOIV(userInformationUpdatedMsg)}
  </div>
);

const scrollToFirstError = (formId: string, errors: FieldErrors<ContactOrPurposeOfUseFormValues>) => {
  const errorFields = Object.keys(errors || {});
  if (errorFields.length > 0 && isInBrowser()) {
    const firstFieldWithError = document
      .getElementById(formId)
      ?.querySelector(`input[name='${errorFields[0]}'], #${errorFields[0]}`);
    if (firstFieldWithError) {
      scrollTo(firstFieldWithError as HTMLElement);
    }
  }
};

export const ContactOrPurposeOfUse = forwardRef(
  (
    {
      id,
      purposeOfUseOrContact,
      isCopiedUserInfo,
      contacts,
      isEmailAndPhoneRequired,
      isCostCenterRequired,
      onChangeContact,
      onCopyUserInfo,
      required,
      enableCostCenterAndReference,
      onlyPurposeOfUse,
    }: ContactOrPurposeOfUseProps,
    ref: ForwardedRef<ContactOrPurposeOfUseCallables>
  ) => {
    const methods = useForm<ContactOrPurposeOfUseFormValues>({
      defaultValues: purposeOfUseOrContactToFormValues(purposeOfUseOrContact),
      mode: 'all',
    });

    const prevPurposeOfUseOrContact = useRef(purposeOfUseOrContact);
    useEffect(() => {
      if (!deepEqual(prevPurposeOfUseOrContact.current, purposeOfUseOrContact)) {
        prevPurposeOfUseOrContact.current = purposeOfUseOrContact;
        methods.reset(purposeOfUseOrContactToFormValues(purposeOfUseOrContact));
      }
    }, [purposeOfUseOrContact]); // eslint-disable-line react-hooks/exhaustive-deps

    useImperativeHandle(
      ref,
      () => ({
        getValues: scrollToError =>
          new Promise<PurposeOfUseOrContact | false>(resolve => {
            methods.handleSubmit(
              values => resolve(formValuesToPurposeOfUseOrContact(values)),
              errors => {
                if (scrollToError) {
                  scrollToFirstError(id, errors);
                }
                resolve(false);
              }
            )();
          }),
      }),
      [methods, id]
    );

    return (
      <FormProvider {...methods}>
        <form onSubmit={e => e.preventDefault()} className="of-contact-or-purpose-of-use" id={id}>
          {isCopiedUserInfo && !methods.formState.isDirty && (
            <CopyUserInfoSuccessMessage purposeOfUseOrContact={purposeOfUseOrContact} />
          )}
          <ContactOrPurposeOfUseSelector
            id={id}
            formattedContacts={contacts}
            isEmailAndPhoneRequired={isEmailAndPhoneRequired}
            onChangeContact={onChangeContact}
            required={required}
            onlyPurposeOfUse={onlyPurposeOfUse}
          />
          {enableCostCenterAndReference && (
            <CostCenterAndEmployeeNumber isCostCenterRequired={isCostCenterRequired} contacts={contacts} />
          )}
          {onCopyUserInfo && (
            <CopyUserInfoButton
              onCopyUserInfo={onCopyUserInfo}
              isEmailAndPhoneRequired={isEmailAndPhoneRequired}
              isCostCenterRequired={isCostCenterRequired}
            />
          )}
        </form>
      </FormProvider>
    );
  }
);
